import { ScopeButton } from '@monorepo/shared-lib';
import { Divider } from 'antd';
import MappingStepOne from 'assets/images/mapping-step-1.svg';
import MappingStepTwo from 'assets/images/mapping-step-2.svg';
import MappingStepThree from 'assets/images/mapping-step-3.svg';
import MappingStepFour from 'assets/images/mapping-step-4.svg';
import { StyledCard } from 'components/Custom/card';
import { CardListSpan, CardTitleLargeSpan } from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultIconClose } from 'shared/styles/common';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
} from 'utils/preferences';
import { MAPPING_TUTORIAL_HIDE } from './map-tax-rates';

export type MappingGuideStep = {
  title: string;
  bullets: string[];
  image: string;
  alt: string;
};

interface MappingGuideProps {
  setShowCard: (show: boolean) => void;
  handlePreferences: (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => any;
  renderTopButtons: () => React.ReactNode;
  /** When omitted, the VAT mapping guide (four steps) is shown. */
  steps?: MappingGuideStep[];
  /** Preference flag written when the guide is closed. */
  hidePreferenceName?: string;
}

export const MappingGuide: React.FC<MappingGuideProps> = ({
  setShowCard,
  handlePreferences,
  renderTopButtons,
  steps,
  hidePreferenceName = MAPPING_TUTORIAL_HIDE,
}) => {
  const { t } = useTranslation();

  const vatSteps: MappingGuideStep[] = [
    {
      title: t('returns.mapping.guide.map-tax-rates-title'),
      bullets: [
        t('returns.mapping.guide.map-tax-rates-step-1'),
        t('returns.mapping.guide.map-tax-rates-step-2'),
        t('returns.mapping.guide.map-tax-rates-step-3'),
      ],
      image: MappingStepOne,
      alt: t('other.mapping-step-1-alt'),
    },
    {
      title: t('returns.mapping.guide.reverse-charge-title'),
      bullets: [
        t('returns.mapping.guide.reverse-charge-step-1'),
        t('returns.mapping.guide.reverse-charge-step-2'),
      ],
      image: MappingStepTwo,
      alt: t('other.mapping-step-2-alt'),
    },
    {
      title: t('returns.mapping.guide.cash-accounting-title'),
      bullets: [
        t('returns.mapping.guide.cash-accounting-step-1'),
        t('returns.mapping.guide.cash-accounting-step-2'),
      ],
      image: MappingStepThree,
      alt: t('other.mapping-step-3-alt'),
    },
    {
      title: t('common.review'),
      bullets: [
        t('returns.mapping.guide.review-step-1'),
        t('returns.mapping.guide.review-step-2'),
        t('returns.mapping.guide.review-step-3'),
      ],
      image: MappingStepFour,
      alt: t('other.mapping-step-4-alt'),
    },
  ];
  const guideSteps = steps ?? vatSteps;

  const handleClose = () => {
    setShowCard(false);
    handlePreferences(PreferenceSections.MAPPING, PreferenceActions.SET, {
      name: hidePreferenceName,
      value: true,
    });
  };

  return (
    <StyledCard
      width="100%"
      spreadcontent
      fullsize
      hideheader
      style={{
        marginBottom: '20px',
        width: '100%',
        position: 'relative',
      }}
      title={
        <div style={{ top: 20, position: 'absolute', left: 25 }}>
          {renderTopButtons()}
        </div>
      }
      extra={
        <DefaultIconClose
          role="button"
          tabIndex={0}
          aria-label={t('common.close')}
          style={{
            top: '25',
            position: 'absolute',
            right: '25',
          }}
          onClick={handleClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClose();
            }
          }}
        />
      }
    >
      <div className="scope-mapping-guide">
        <Divider />
        <div className="scope-mapping-guide-steps">
          {guideSteps.map((step) => (
            <div key={step.title} className="scope-mapping-guide-step">
              <div className="scope-mapping-guide-copy">
                <CardTitleLargeSpan className="scope-mapping-guide-title">
                  {step.title}
                </CardTitleLargeSpan>
                <CardListSpan>
                  <ul className="scope-mapping-guide-list">
                    {step.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </CardListSpan>
              </div>
              <div className="scope-mapping-guide-figure">
                <img alt={step.alt} src={step.image} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <ScopeButton
        type="default"
        onClick={handleClose}
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          width: 'auto',
          height: 28,
          fontSize: '12px',
          padding: '0 12px',
        }}
      >
        {t('common.close')}
      </ScopeButton>
    </StyledCard>
  );
};
